import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import RequestStatusTimeline from "components/DesignSystem/RequestStatusTimeline";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import {
  designColors,
  designShell,
} from "services/constants/designTokens";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { canonicalOnDutyStatus, onDutyStatusVisual } from "./OnDutyStatusCards";
import { Apply as OnDutyApplyPanel } from "./Apply";

const toISODate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

const detailAccent = {
  date: detailAccentPalette.indigo,
  startTime: detailAccentPalette.blue,
  endTime: detailAccentPalette.green,
  reason: detailAccentPalette.orange,
  rejectReason: detailAccentPalette.purple,
};

export const OnDutyDetailPanel = ({
  open,
  onClose,
  onDuty,
  checkAccess,
  setReCallApi,
  expanded = false,
  onToggleExpand,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const goToApprovalCenter = useGoToApprovalCenter();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  // No dedicated cancel-an-approved-on-duty endpoint exists yet, same gap as
  // Comp Off (see CompOffDetailPanel) — this reuses the same approve/reject
  // endpoint below with status: "Cancelled" as a first attempt, needs
  // verifying against the real backend.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // See Leave/LeaveDetailPanel.js — Approve/Reject/Cancel used to be gated
  // by plain editor permission (checkAccess). Now requires real approver
  // permission (either level counts).
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "On Duty",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "On Duty",
    );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: { rejected_reason: "" },
  });

  useEffect(() => {
    setRejectDialogOpen(false);
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
    setEditPanelOpen(false);
    reset({ rejected_reason: "" });
  }, [onDuty, reset]);

  useEffect(() => {
    register("rejected_reason", {
      required: "Reject reason is required",
      validate: {
        minLengthNoSpaces: (value) => {
          if (value?.trim().length < 3) return minimum3Characters;
          if (value?.trim().length > 255) return "Maximum length is 255 characters";
          return true;
        },
        atleastOneCharacterWithSpaceReg: (value) =>
          atleastOneCharacterWithSpace.test(value) || "Atleast one alphabet required",
      },
    });
  }, [register]);

  const statusFlowData = useMemo(
    () => ({
      ...(onDuty || {}),
      applied_by:
        onDuty?.applied_by ||
        onDuty?.employee_name ||
        onDuty?.username ||
        onDuty?.requested_by,
      applied_on:
        onDuty?.applied_on ||
        onDuty?.applied_date ||
        onDuty?.created_at ||
        onDuty?.created_date ||
        onDuty?.date,
      // onDuty?.approver_name is the assigned approver, stamped at apply
      // time before anyone has acted -- it is not proof of approval and
      // must not be preferred over (or substitute for) the backend's
      // status-derived approved_by.
      approved_by: onDuty?.approved_by,
      approved_at: onDuty?.approved_at || onDuty?.approved_on,
      rejected_by: onDuty?.rejected_by,
      rejected_at: onDuty?.rejected_at || onDuty?.rejected_on,
    }),
    [onDuty],
  );

  if (!onDuty) return null;

  const status = canonicalOnDutyStatus(onDuty?.status);
  const isPending = status === "Pending";
  // Requested per user: allow cancelling an on duty request even after it
  // has been Approved (not just while Pending), same as Comp Off.
  const canCancelApproved = status === "Approved";
  const employeeName = onDuty?.employee_name || "N/A";
  const employeeId = onDuty?.employee_id || onDuty?.emp_id || "-";
  const department = onDuty?.department_name || onDuty?.department || "-";
  const designation = onDuty?.designation_name || onDuty?.designation || "-";
  const requestReason = onDuty?.reason || onDuty?.description || "-";
  const rejectedReason = onDuty?.rejected_reason || "";
  const decisionReasonLabel =
    status === "Cancelled" ? "Cancel Reason" : "Reject Reason";

  const handleCloseAction = () => {
    onClose?.();
  };

  const handleEditDone = () => {
    setEditPanelOpen(false);
    setReCallApi?.(true);
    onClose?.();
  };

  const updateOnDutyStatus = async (requestStatus, reason) => {
    const payload = {
      id: onDuty.id,
      user_id: onDuty.user_id,
      attendance_type: "On Duty",
      reason: onDuty?.reason,
      status: requestStatus,
      date: toISODate(onDuty?.date),
      start_time: onDuty?.start_time,
      end_time: onDuty?.end_time,
      ...(requestStatus === "Rejected" && { rejected_reason: reason }),
      ...(requestStatus === "Cancelled" && { cancelled_reason: reason }),
    };
    const response = await customAPI(
      `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?company_id=${requestCompanyId}`,
      "PUT",
      payload,
    );
    if (response?.ok) {
      showToast(
        requestStatus === "Rejected" ? "error" : "success",
        response?.responseDetails?.success?.[0]?.message ||
          `On duty request ${requestStatus.toLowerCase()}.`,
      );
      setReCallApi?.(true);
      setRejectDialogOpen(false);
      setCancelDialogOpen(false);
      onClose?.();
      return true;
    }
    showToast(
      "error",
      response?.message ||
        `Couldn't ${
          requestStatus === "Approved"
            ? "approve"
            : requestStatus === "Cancelled"
              ? "cancel"
              : "reject"
        } the on duty request. Please try again.`,
    );
    return false;
  };

  const handleApprove = async () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this on duty request",
      );
      return;
    }
    setApproving(true);
    await updateOnDutyStatus("Approved");
    setApproving(false);
  };

  const submitReject = async (data) => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to reject this on duty request",
      );
      return;
    }
    setRejecting(true);
    await updateOnDutyStatus("Rejected", data?.rejected_reason);
    setRejecting(false);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    reset({ rejected_reason: "" });
    clearErrors("rejected_reason");
  };

  const submitCancel = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to cancel this on duty request",
      );
      return;
    }
    setCancelling(true);
    await updateOnDutyStatus("Cancelled", trimmed);
    setCancelling(false);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  // Reject/Approve/Cancel below used to always render, only disabled (via
  // AccessGatedAction) when hasApproverAccess was false — leaving a
  // bordered footer strip with unusable buttons for an editor-only viewer.
  // Each branch now resolves to real content or a genuine `null` (same fix
  // as OverTime/DetailPanel.js) so GlobalSidePanel's own footer border only
  // draws when there's actually something to show.
  const footerContent = isPending ? (
    hasApproverAccess ? (
      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1.5,
        }}
      >
        <Mui.Button
          variant="outlined"
          startIcon={<HighlightOffIcon />}
          onClick={() => setRejectDialogOpen(true)}
          sx={{
            ...designShell.panelDangerButton,
            color: "#B42318",
            borderColor: "#F5C2C7",
            backgroundColor: "#FEF3F2",
            "&:hover": {
              borderColor: "#E59CA5",
              backgroundColor: "#FEE4E2",
            },
          }}
        >
          Reject
        </Mui.Button>
        <PrimaryButton
          startIcon={<CheckCircleOutlineIcon />}
          loading={approving}
          onClick={handleApprove}
        >
          Approve
        </PrimaryButton>
      </Mui.Box>
    ) : null
  ) : canCancelApproved ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5}>
        <Mui.Typography
          sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
        >
          This request has already been approved.
        </Mui.Typography>
        <RequestCancelButton onClick={() => setCancelDialogOpen(true)}>
          Cancel On Duty
        </RequestCancelButton>
      </Mui.Stack>
    ) : null
  ) : (
    <Mui.Typography
      sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
    >
      This request has already been {status?.toLowerCase() || "processed"}.
    </Mui.Typography>
  );

  const rejectDialog = (
    <GlobalConfirmDialog
      open={rejectDialogOpen}
      title="Reject On Duty Request"
      onCancel={closeRejectDialog}
      onConfirm={() => {
        if (!watch("rejected_reason")?.trim()) {
          setError("rejected_reason", { type: "required", message: "Reject reason is required" });
          return;
        }
        handleSubmit(submitReject)();
      }}
      confirmLabel="Reject"
      cancelLabel="Cancel"
      loading={rejecting}
      danger
      reasonValue={watch("rejected_reason") || ""}
      onReasonChange={(value) => setValue("rejected_reason", value, { shouldValidate: true })}
      reasonError={errors?.rejected_reason?.message || ""}
      reasonPlaceholder="Provide a reason for rejection"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Enter the reason for rejecting this On Duty request.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const cancelDialog = (
    <GlobalConfirmDialog
      open={cancelDialogOpen}
      title="Cancel On Duty Request"
      onCancel={closeCancelDialog}
      onConfirm={submitCancel}
      confirmLabel="Continue"
      cancelLabel="Keep"
      loading={cancelling}
      loadingLabel="Cancelling..."
      danger
      reasonValue={cancelReason}
      onReasonChange={(value) => {
        setCancelReason(value);
        if (cancelReasonError) setCancelReasonError("");
      }}
      reasonError={cancelReasonError}
      reasonPlaceholder="Provide a reason for cancelling this approved on duty request"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        This on duty request has already been approved. Enter a reason to
        cancel it.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={handleCloseAction}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="On Duty Request"
        subtitle="Request details and status"
        headerActions={
          isPending && checkAccess ? (
            <Mui.Tooltip title="Edit on duty request" arrow>
              <Mui.IconButton
                aria-label="Edit on duty request"
                size="small"
                onClick={() => setEditPanelOpen(true)}
                sx={{
                  ...designShell.panelSoftIconButton,
                  color: designColors.slate500,
                }}
              >
                <EditIcon fontSize="small" />
              </Mui.IconButton>
            </Mui.Tooltip>
          ) : null
        }
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "On Duty", onClick: handleCloseAction },
          employeeName || "On Duty Request",
        ]}
        footer={footerContent}
      >
        <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <RequestDetailHeader
            userDetails={{
              image_url: onDuty?.image_url,
              employee_id: employeeId,
              user_name: employeeName,
              department_name: department,
              designation,
            }}
            status={status}
            colorCode={onDuty?.color_code || onDutyStatusVisual(status).accent}
          />

          <Mui.Box sx={detailCardSx}>
            <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
              Request Details
            </Mui.Typography>
            <RequestDetailField
              icon={CalendarMonthIcon}
              label="Date"
              value={onDuty?.date || "-"}
              accent={detailAccent.date}
            />
            <RequestDetailField
              icon={AccessTimeIcon}
              label="Start Time"
              value={onDuty?.start_time || "-"}
              accent={detailAccent.startTime}
            />
            <RequestDetailField
              icon={AccessTimeIcon}
              label="End Time"
              value={onDuty?.end_time || "-"}
              accent={detailAccent.endTime}
            />
            <RequestDetailField
              icon={DescriptionOutlinedIcon}
              label="Reason"
              value={requestReason}
              accent={detailAccent.reason}
              multiline
              isLast={!rejectedReason}
            />
            {rejectedReason ? (
              <RequestDetailField
                icon={DescriptionOutlinedIcon}
                label={decisionReasonLabel}
                value={rejectedReason}
                accent={detailAccent.rejectReason}
                multiline
                isLast
              />
            ) : null}
          </Mui.Box>

          <RequestStatusTimeline
            endDate={onDuty?.date}
            data={statusFlowData}
            variant="stepper"
            hidePendingStep
          />
          <ApprovalEditAuditTrail data={onDuty} />
        </Mui.Box>
      </GlobalSidePanel>
      {editPanelOpen ? (
        <OnDutyApplyPanel
          open={editPanelOpen}
          onClose={() => setEditPanelOpen(false)}
          setReCallApi={handleEditDone}
          editRecord={onDuty}
        />
      ) : null}
      {rejectDialog}
      {cancelDialog}
    </>
  );
};
